import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';
import { SearchSelect } from '../../components/search-select.js';
import { Button, Callout } from '../../components/ui.js';
import { api, errorMessage } from '../../lib/api.js';
import { brandFromVendor } from './brand-from-vendor.js';

/**
 * Pick a brand, or make the one that is missing (decision 119).
 *
 * The shop's export carried no brands, so the list this picker draws from was
 * empty on staging and two long on production — and nothing anywhere could add
 * to it. The finish pass asked 4,000 styles for a brand from a list with
 * nothing in it, which is a dead end with a season behind it (§2.2-11), and
 * the reviewer said so: "there is no option or unable to enter the brand".
 *
 * So the person who finds a brand missing makes it where they stand, the same
 * way a category is made mid-import: one field, one press, and the new brand
 * is selected. With an empty list the field is already open, because "pick
 * from nothing" is not a choice. Same gate as creating a style
 * (`catalog.manage`), enforced by the server.
 */
export function BrandPicker({
  value,
  onChange,
  brands,
  optional = false,
  vendorName = null,
}: {
  value: string;
  onChange: (value: string) => void;
  brands: ReadonlyArray<{ id: string; name: string }>;
  optional?: boolean;
  /** The style's vendor: most of this shop's vendors are the maker, so its name is offered. */
  vendorName?: string | null;
}) {
  const queryClient = useQueryClient();
  const [adding, setAdding] = useState(false);
  const suggested = brandFromVendor(vendorName);
  /** The vendor's name is already a brand: one press to use it. */
  const existing =
    suggested === null
      ? undefined
      : brands.find((brand) => brand.name.toLowerCase() === suggested.toLowerCase());
  const [name, setName] = useState(existing === undefined ? (suggested ?? '') : '');
  const open = adding || brands.length === 0;

  const create = useMutation({
    mutationFn: () => api.products.createBrand.mutate({ name: name.trim() }),
    onSuccess: async (created) => {
      await queryClient.invalidateQueries({ queryKey: ['products', 'taxonomy'] });
      onChange(created.id);
      setName('');
      setAdding(false);
    },
  });

  return (
    <div className="brand-picker">
      <div className="field">
        <label className="field__label" htmlFor="brand-picker-choice">
          Brand
        </label>
        {/* Searchable: the list grows to a hundred once vendors become brands. */}
        <SearchSelect
          id="brand-picker-choice"
          value={value}
          onChange={onChange}
          placeholder="Pick a brand — type to search"
          {...(optional ? { noneLabel: 'None' } : {})}
          options={brands.map((brand) => ({ value: brand.id, label: brand.name }))}
        />
        {existing !== undefined && value !== existing.id ? (
          <button type="button" className="link-button" onClick={() => onChange(existing.id)}>
            Use {existing.name}, the vendor&rsquo;s name
          </button>
        ) : null}
      </div>
      {open ? (
        <div className="brand-picker__add">
          <label className="field__label" htmlFor="brand-picker-name">
            New brand
          </label>
          <div className="brand-picker__row">
            <input
              id="brand-picker-name"
              className="input"
              value={name}
              maxLength={60}
              placeholder="Obermeyer"
              onChange={(event) => setName(event.target.value)}
              onKeyDown={(event) => {
                if (event.key === 'Enter' && name.trim() !== '') {
                  event.preventDefault();
                  create.mutate();
                }
              }}
            />
            <Button
              variant="secondary"
              busy={create.isPending}
              disabledReason={name.trim() === '' ? 'Type the brand’s name' : undefined}
              onClick={() => create.mutate()}
            >
              Add brand
            </Button>
          </div>
          {create.isError ? <Callout tone="danger">{errorMessage(create.error)}</Callout> : null}
        </div>
      ) : (
        <button type="button" className="link-button" onClick={() => setAdding(true)}>
          Brand not listed? Add it
        </button>
      )}
    </div>
  );
}
